export default {
  // Plugins
  async getPlugins(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/plugins${query ? '?' + query : ''}`);
  },

  /** Whether this plugin is connected to its provider, and to which account. */
  async getPluginOAuth(pluginId, groupId = null) {
    const q = groupId ? `?group_id=${encodeURIComponent(groupId)}` : '';
    return this.request(`/plugins/${pluginId}/oauth${q}`);
  },

  /** Begin an authorisation. Returns where to send the person — it does not go. */
  async startPluginOAuth(pluginId, { groupId = null, returnTo = null } = {}) {
    return this.request(`/plugins/${pluginId}/oauth/start`, {
      method: 'POST',
      body: {
        ...(groupId ? { group_id: groupId } : {}),
        // A PATH, never an absolute URL. The backend drops anything else, because
        // an absolute return_to is how the end of an OAuth flow becomes somebody
        // else's landing page.
        ...(returnTo ? { return_to: returnTo } : {}),
      },
    });
  },

  async disconnectPluginOAuth(pluginId, groupId = null) {
    const q = groupId ? `?group_id=${encodeURIComponent(groupId)}` : '';
    return this.request(`/plugins/${pluginId}/oauth${q}`, { method: 'DELETE' });
  },

  /** Recent runs across every plugin automation, newest first. */
  async getPluginRuns(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/plugin-runs${query ? '?' + query : ''}`);
  },

  /** Is this plugin set up, and is it running? States only — never a value. */
  async getPluginStatus(pluginId, groupId = null) {
    const q = groupId ? `?group_id=${encodeURIComponent(groupId)}` : '';
    return this.request(`/plugins/${pluginId}/status${q}`);
  },

  /**
   * getPluginStatus for many plugins: one request per 200 ids, not one per
   * plugin. Resolves to { [id]: status | null } — null where that plugin was
   * refused or its chunk failed, so one bad answer costs a plugin its state,
   * not the list its existence.
   */
  async getPluginStatuses(pluginIds, groupId = null) {
    const ids = [...new Set((pluginIds || []).map(String))];
    const chunks = [];
    for (let i = 0; i < ids.length; i += 200) chunks.push(ids.slice(i, i + 200));
    const out = {};
    await Promise.all(chunks.map(async (chunk) => {
      const q = new URLSearchParams({ ids: chunk.join(',') });
      if (groupId) q.set('group_id', groupId);
      const r = await this.request(`/plugins/status?${q}`).catch(() => null);
      for (const id of chunk) {
        const st = r?.statuses?.[id];
        out[id] = st && !st.error ? st : null;
      }
    }));
    return out;
  },

  async executePlugin(pluginId, config = {}) {
    return this.request(`/plugins/${pluginId}/execute`, {
      method: 'POST',
      body: config,
    });
  },

  // Sub-Entity Plugins
  async getMySubEntityPlugins(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/plugins/sub-entity/mine${query ? '?' + query : ''}`);
  },

  async getGroupSubEntityPlugins(groupId) {
    return this.request(`/plugins/sub-entity/mine?group_id=${groupId}`);
  },

  // Automations
  async getAutomations(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/automations${query ? '?' + query : ''}`);
  },

  async createAutomation(data) {
    return this.request('/automations', {
      method: 'POST',
      body: data,
    });
  },

  async updateAutomation(automationId, updates) {
    return this.request(`/automations/${automationId}`, {
      method: 'PUT',
      body: updates,
    });
  },

  async deleteAutomation(automationId) {
    return this.request(`/automations/${automationId}`, {
      method: 'DELETE',
    });
  },

  async triggerAutomation(automationId) {
    return this.request(`/automations/${automationId}/trigger`, {
      method: 'POST',
    });
  },

  async activateAutomation(automationId) {
    return this.request(`/automations/${automationId}/activate`, {
      method: 'POST',
    });
  },

  async deactivateAutomation(automationId) {
    return this.request(`/automations/${automationId}/deactivate`, {
      method: 'POST',
    });
  },
};
